import { Component, OnInit, inject, effect, signal, ChangeDetectionStrategy, computed } from '@angular/core';
import { Backend } from '../../backend-interface/backend';
import { CompromiseReports, BwFilters, BwObjectPromise } from '../../service/global.service';
import { DatePipe } from '@angular/common';
import { ToastService } from 'src/app/service/toast.service';
import { HttpErrorResponse } from '@angular/common/http';
import { AccessManager, database } from 'src/app/acces_manager';
import { TranslationService } from 'src/app/service/translation.service';
import { NgbPaginationModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { LoadingErrorComponent } from 'src/app/components/loading-error/loading-error.component';
import { ExistObjectPipe } from 'src/app/pipes/filter.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { ToastComponent } from 'src/app/components/toast/toast.component';
import { DatepickerComponent } from 'src/app/components/datepicker/datepicker.component';
import { FiltersACComponent } from 'src/app/components/filters/filters-ac/filters-ac.component';
import { CompromiseReportModalComponent } from './compromise-report-modal/compromise-report-modal.component';

@Component({
  selector: 'app-liste-compromise-reports',
  templateUrl: './liste-compromise-reports.component.html',
  styleUrls: ['./liste-compromise-reports.component.scss'],
  standalone: true,
  providers: [DatePipe, ToastService, TranslationService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    NgbPaginationModule,
    NgbTooltipModule,
    LoadingErrorComponent,
    ExistObjectPipe,
    ToastComponent,
    DatepickerComponent,
    FiltersACComponent,
    CompromiseReportModalComponent
  ]
})
export class ListeCompromiseReportsComponent implements OnInit {
  readonly database: AccessManager = database;
  listAll = signal<CompromiseReports[]>([]);
  listBorn = signal<CompromiseReports[]>([]);
  loadingOerror = signal(true);
  promiseResult?: BwObjectPromise;
  filtersActive = signal<BwFilters>({ pageSize: 50 });
  searchmodel = signal('');
  detectorsList = false;
  comproCourante = signal<CompromiseReports | undefined>(undefined);
  page = signal(1);
  pageSize = signal(this.filtersActive().pageSize ?? 50);
  predId = '';

  private readonly translationService = inject(TranslationService);
  private readonly toastService = inject(ToastService);
  private readonly datepipe = inject(DatePipe);

  readonly dateRangeTitle = computed(() => {
    const filters = this.filtersActive();
    if (filters.from && filters.to) {
      return this.titrefiltreDate(filters.from, filters.to).toLowerCase();
    }
    return '';
  });

  readonly compromiseDateRange = computed(() => {
    const born = this.listBorn();
    if (born.length > 0) {
      return `${this.datepipe.transform(born[born.length - 1].createdAt, this.translationService.getDateFormat())} - ${this.datepipe.transform(born[0].createdAt, this.translationService.getDateFormat())}`;
    }
    return '';
  });

  constructor() {
    effect(() => {
      this.translationService.get('menu-compromise-reports').subscribe(title => {
        document.title = `DIP - ${title}`;
      });
    });
  }

  ngOnInit(): void {
    this.getDefaultList();
  }

  async getDefaultList(): Promise<void> {
    const savedFilters = this.database.getdata('compromiseFilters');
    if (savedFilters) {
      this.filtersActive.set(savedFilters);
      await this.searchRequestFilters(this.filtersActive());
    } else {
      await this.searchResultWithTimeStamp();
      await this.loading(0.5);
    }
  }

  async loading(seconds: number): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, seconds * 1000));
    this.loadingOerror.set(false);
  }

  async searchCompromiseReports(filters: BwFilters): Promise<BwObjectPromise> {
    return Backend.COMPROMISE_REPORTS.search(filters);
  }

  async searchResultWithTimeStamp(): Promise<void> {
    const timeIntervals = [86400000, 604800000, 1209600000];
    const filters = { ...this.filtersActive(), to: Date.now() };
    for (const interval of timeIntervals) {
      filters.from = filters.to - interval;
      this.promiseResult = await this.searchCompromiseReports(filters);
      if (this.promiseResult.results) {
        this.listBorn.set(JSON.parse(this.promiseResult.results));
        this.filtersActive.set(filters);
        return;
      }
    }
    await this.handleNoResults();
  }

  async handleNoResults(): Promise<void> {
    const filters = { ...this.filtersActive(), from: undefined, to: undefined };
    this.filtersActive.set(filters);
    await this.getAllCompromission();
    this.listBorn.set(this.listAll());
    if (this.listBorn().length > 0) {
      this.filtersActive.set({ ...filters, from: 1577833200000, to: this.listBorn()[0].createdAt });
    }
  }

  async resetAll(): Promise<void> {
    this.filtersActive.set({ pageSize: 50 });
    this.loadingOerror.set(true);
    this.searchmodel.set('');
    await this.searchResultWithTimeStamp();
    await this.loading(0.15);
    this.database.setdata('compromiseFilters', this.filtersActive());
  }

  async getAllCompromission(): Promise<void> {
    this.promiseResult = await this.searchCompromiseReports(this.filtersActive());
    this.listAll.set(this.promiseResult.results ? JSON.parse(this.promiseResult.results) : []);
  }


  async searchByTimestamp([from, to]: [number, number]): Promise<void> {
    const filters = { ...this.filtersActive(), from, to };
    this.filtersActive.set(filters);
    this.patchMainFilters();
    this.searchmodel.set('');
    this.loadingOerror.set(true);
    this.page.set(1);
    this.promiseResult = await this.searchCompromiseReports(this.filtersActive());
    this.patchCurrentList(this.promiseResult);
    this.database.setdata('compromiseFilters', this.filtersActive());
    this.unsetComproSelectionnee();
    await this.loading(0.15);
  }

  async nextPage(page: number): Promise<void> {
    this.loadingOerror.set(true);
    this.filtersActive.set({ ...this.filtersActive(), page });
    this.page.set(page);
    this.promiseResult = await this.searchCompromiseReports(this.filtersActive());
    this.patchCurrentList(this.promiseResult);
    this.unsetComproSelectionnee();
    await this.loading(0.15);
  }

  patchCurrentList(compromisePromise: BwObjectPromise): void {
    this.listBorn.set(compromisePromise.results ? JSON.parse(compromisePromise.results) : []);
  }

  async filtreAppNamefromAll(nom: string): Promise<void> {
    this.loadingOerror.set(true);
    this.promiseResult = await Backend.BW_FILTRES.filtreAppFromAll(this.promiseResult, nom, 'compro');
    if (this.promiseResult) {
      this.patchCurrentList(this.promiseResult);
      this.page.set(1);
      const savedFilters = this.database.getdata('compromiseFilters');
      if (savedFilters) {
        this.filtersActive.set(savedFilters);
      }
      this.loadingOerror.set(false);
    }
  }

  awaitChargementListe(): boolean {
    return this.listBorn().length > 0;
  }
  formatAPPName(appName: string): string {
    return Backend.FORMAT.formatAPPName(appName);
  }
  formatName(model: string): string {
    return Backend.FORMAT.formatName(model);
  }
  timestampToDate(time?: number): string {
    return Backend.FORMAT.timestampToDate(time, this.datepipe,  this.translationService.getDatePipeLang()) ?? '';
  }
  horaire(timestamp: number): string {
    return Backend.FORMAT.horaire(timestamp, this.datepipe) ?? '';
  }
  tooltip(bwCompromiseType: string): string {
    if (!bwCompromiseType) return 'Unknown Type';
    const type = bwCompromiseType.toLowerCase();
    if (type === 'panic') return 'Panic';
    if (type === 'boot' || type === 'boot_ko') return 'Boot KO';
    if (type === 'error') return 'Error';
    return 'Unknown Type';
  }
  setComproSelectionnee(compro: CompromiseReports): void {
    this.comproCourante.set(compro);
  }
  unsetComproSelectionnee(): void {
    this.comproCourante.set(undefined);
  }
  scroolTo(): void {
    document.getElementById('detectorsInfos')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  readonly listeNomsProbesIOS = [
    'utils', 'behavior', 'debugger', 'virtualisation', 'memory', 'jailbreak', 'hook', 'integrity', 'status', 'sensitive', 'black&White', 'hardware', 'software', 'sensors'
  ];
  readonly listeNomsProbesAndroid = [
    'utils', 'behavior', 'debugger', 'virtualisation', 'memory', 'root', 'hook', 'integrity', 'status', 'sensitive', 'black&White', 'hardware', 'software', 'sensors'
  ];
  getAllProbes(): string[] {
    const platform = this.comproCourante()?.deviceInfo?.platformName?.toLowerCase();
    if (platform === 'android') return this.listeNomsProbesAndroid;
    if (platform === 'ios') return this.listeNomsProbesIOS;
    return [];
  }
  statutProbe(family: string): string {
    const compro = this.comproCourante();
    if (!compro?.detectors) return 'probe-ok';
    const familyLower = family.toLowerCase();
    return compro.detectors.some(detector => {
      const detectorFamily = detector.family?.toLowerCase();
      return detectorFamily === familyLower || (familyLower === 'jailbreak' && detectorFamily === 'root');
    }) ? 'probe-fatal' : 'probe-ok';
  }
  today(): number {
    return Date.now();
  }
  titrefiltreDate(debut: number, fin: number): string {
    return Backend.FORMAT.formatDate(debut, fin);
  }
  formatId(id: string): string {
    return Backend.FORMAT.formatId(id);
  }
  formatLongString(n: number, chaine?: string): string {
    return Backend.FORMAT.formatN(chaine ?? '', n) ?? '';
  }
  majFormID(event: Event, id: string): string {
    return event ? this.formatId(id) : id;
  }
  formatType(bwCompromiseType: unknown): string {
    return typeof bwCompromiseType === 'string' ? bwCompromiseType.toLowerCase().replace(/ |-|_|ko/g, '') : '';
  }
  async searchRequestFilters(event: BwFilters): Promise<void> {
    this.loadingOerror.set(true);
    this.patchSearchModel(event);
    this.patchMainFilters(event);
    try {
      this.promiseResult = await this.searchCompromiseReports(this.filtersActive());
      this.patchCurrentList(this.promiseResult);
      this.database.setdata('compromiseFilters', this.filtersActive);
      await this.loading(0.5);
    } catch (e) {
      const err = e as HttpErrorResponse;
      this.toastService.show(err.error?.message ?? 'Erreur inconnue', { classname: 'bg-danger', delay: 6000 });
      await this.resetAll();
    }
    this.unsetComproSelectionnee();
  }
  patchMainFilters(filters?: BwFilters): void {
    this.filtersActive.set(Backend.BW_FILTRES.patchMainFilters(this.filtersActive(), filters));
  }
  patchSearchModel(event: BwFilters): void {
    this.searchmodel.set(event.bwAppId || event.id || event.deviceId || '');
  }
}
